import EventEmitter from '../classes/EventEmiter'
import type {
  Icomponent,
  IContainers,
  IProcessedComponent,
  IProcessedComponentProps,
  IProcessedContainer,
  UnresolvedRecord,
} from '../interfaces'
import { CheckoutRules } from '../interfaces'
import { RootState } from '../redux/store'
import { processActions } from '../utils/processActions'
import processData from '../utils/processData'
import { getMinAmountProgressMessage } from '../utils/minAmountProgressMessage'
import { getCartConditionBlockedAlert } from '../utils/cartConditionBlockedAlert'
import { isHidePricesEnabled } from '../utils/hidePrices'
import maskPricesForDisplay from '../utils/maskPricesForDisplay'
import { getCartCheckoutPanelProps } from '../utils/cartCheckoutPanel'
import {
  getOrderDetailPanelProps,
  VERBATIM_DETAIL_PROPS,
} from '../utils/orderDetailPanel'
import {
  getOrderConfirmationPanelProps,
  VERBATIM_CONFIRMATION_PROPS,
} from '../utils/orderConfirmationPanel'
import { getDeliveryDatePickerProps } from '../utils/deliveryDatePicker'
import { getPaymentMethodSelectorProps } from '../utils/paymentMethodSelector'
import { getLoyaltyPointsEarnedProps } from '../utils/loyaltyPointsEarned'
import { DefaultCheckoutRulesNames } from '../consts/defaultConfigValues/defaultConstants'

const MIN_AMOUNT_IMPORT_ROUTE = 'MinAmount'

/**
 * The uid of the cart-condition-blocked Alert entry authored in
 * `defaultConfig.ts` under `ShoppingCart.body.components` (COM-1213).
 *
 * Keyed on uid rather than on `importRoute === 'Alert'` because the
 * ShoppingCart already renders three other `Alert` entries (MinQty, MaxQty,
 * MaxAmount), and this injector must only touch the one it owns.
 */
const CART_CONDITION_BLOCKED_ALERT_UID = 'comp-cart-condition-blocked'

/**
 * UIDs of the client-local cart-condition alerts, and the server rule type
 * each one covers (COM-1213).
 *
 * Reviewer of #826 called out the gap: `cartAlerts.*.isActive` is written by
 * `onCartResponse`, so between a pre-flight answer arriving and the next cart
 * mutation, both the new server-rule alert and the local one render. This
 * render-time suppression closes that window — when `getCartConditionBlockedAlert`
 * is about to render for a rule type, the matching local Alert's `show` is
 * flipped to `false` here, regardless of what `cartAlerts.isActive` says.
 *
 * `MinAmount` (footer) uses `MIN_AMOUNT_IMPORT_ROUTE` rather than a `uid`
 * match in the lookup — the three body Alerts are the only ones that key on
 * uid. The uid-based entries carry the lookup key as the uid; the footer
 * entry is handled through `isMinAmount` + the rule-type lookup below.
 */
const LOCAL_CART_CONDITION_ALERT_UID_TO_RULE: ReadonlyMap<
  string,
  CheckoutRules
> = new Map([
  [
    DefaultCheckoutRulesNames.MIN_QTY_CART_CHECKOUT_RULE,
    CheckoutRules.CART_CONDITION_PENDING_MIN_QTY,
  ],
  [
    DefaultCheckoutRulesNames.MAX_QTY_CART_CHECKOUT_RULE,
    CheckoutRules.CART_CONDITION_PENDING_MAX_QTY,
  ],
  [
    DefaultCheckoutRulesNames.MAX_AMOUNT_CART_CHECKOUT_RULE,
    CheckoutRules.CART_CONDITION_PENDING_MAX_AMOUNT,
  ],
])

/**
 * `props` is the component's own authored props, and only `CartCheckoutPanel`
 * reads them — for `variant`, to tell the card above the item list from the
 * repeat inside the totals block. Every other injector ignores it.
 *
 * A second parameter rather than a field on one argument object, so an
 * injector that wants only state can declare only state, which the rest do.
 */
type PanelPropInjector = (
  state: RootState,
  props: UnresolvedRecord,
) => UnresolvedRecord

/**
 * Components whose props cinnamon partly owns, and the injector for each.
 *
 * A lookup rather than a chain of `importRoute ===` ternaries, because there
 * is more than one. A route with no entry is left entirely to config, which is
 * every other component in the library.
 *
 * No count here: this comment said "two", was stale at three, and would be
 * stale again at the next entry. Count the `Map` if you need the number.
 *
 * A `Map` rather than an object literal, for two reasons that both come from
 * `importRoute` being an arbitrary string out of remote JSON. `Map.get` returns
 * `PanelPropInjector | undefined`, so the guard at the call site is typed
 * honestly — an object index signature types as never-undefined under this
 * repo's `strict` (no `noUncheckedIndexedAccess`), which makes a load-bearing
 * guard read as dead code to the next person. And a `Map`'s entries are not
 * looked up on a prototype chain, so an `importRoute` of `'constructor'`
 * finds nothing — where an object literal resolves it to
 * `Object.prototype.constructor`, and `Object(state)` returns `state`, which
 * would spread the entire store into one component's props.
 */
const PANEL_PROP_INJECTORS = new Map<string, PanelPropInjector>([
  ['CartCheckoutPanel', getCartCheckoutPanelProps],
  ['OrderDetailPanel', getOrderDetailPanelProps],
  ['OrderConfirmationPanel', getOrderConfirmationPanelProps],
  ['PaymentMethodSelector', getPaymentMethodSelectorProps],
  ['DeliveryDateCalendar', getDeliveryDatePickerProps],
  ['LoyaltyPointsEarned', getLoyaltyPointsEarnedProps],
])

/**
 * Per component, the injected props that must reach it verbatim: assigned
 * after `processData`, masking and `processActions` rather than through them.
 * For server-authored text only, where a `{{…}}` is the server's words and
 * not a binding. See `VERBATIM_CONFIRMATION_PROPS` for why, and for why the
 * price masking is unaffected.
 */
const VERBATIM_PANEL_PROPS = new Map<string, readonly string[]>([
  ['OrderConfirmationPanel', VERBATIM_CONFIRMATION_PROPS],
  ['OrderDetailPanel', VERBATIM_DETAIL_PROPS],
])

/** Splits `record` into the keys in `keys` and the rest. */
const splitVerbatim = (
  record: UnresolvedRecord,
  keys: readonly string[],
): { toResolve: UnresolvedRecord; verbatim: UnresolvedRecord } => {
  const toResolve: UnresolvedRecord = { ...record }
  const verbatim: UnresolvedRecord = {}
  keys.forEach((key) => {
    verbatim[key] = toResolve[key]
    delete toResolve[key]
  })
  return { toResolve, verbatim }
}

const resolveProps = (
  data: UnresolvedRecord,
  state: RootState,
  preserveTotal = false,
) => {
  const resolved = processData(data, { state })
  if (!isHidePricesEnabled(state)) return resolved
  return maskPricesForDisplay(resolved, preserveTotal)
}

const getCinnamonProps = <T = unknown>(
  props: UnresolvedRecord,
  state: RootState,
  eventEmitter: EventEmitter,
  preserveTotal = false,
): IProcessedComponentProps<T> => {
  const cinnamonProps = resolveProps(props, state, preserveTotal)
  return processActions(cinnamonProps, eventEmitter)
}

const mapComponent =
  (
    state: RootState,
    eventEmitter: EventEmitter,
    hasCartConditionBlockedAlertComponent: boolean,
  ) =>
  ({ props = {}, ...rest }: Icomponent): IProcessedComponent => {
    const isMinAmount = rest.importRoute === MIN_AMOUNT_IMPORT_ROUTE
    const isCartConditionBlocked =
      rest.importRoute === 'Alert' &&
      rest.uid === CART_CONDITION_BLOCKED_ALERT_UID
    /*
     * The props the order panels own rather than the config author.
     *
     * Not enumerated here, deliberately. The two authoritative lists are
     * `InjectedDetailProps` and `InjectedConfirmationProps`, in
     * `utils/orderDetailPanel.ts` and `utils/orderConfirmationPanel.ts`, and
     * each is a `Pick` over the library's own prop type — so a name that
     * drifts fails the build, where a prose copy of the same list fails
     * nothing. This comment used to carry such a copy and went stale twice,
     * once for `timeZone` and once for `distributor`.
     *
     * What is true of all of them: none is expressible as a `{{variable}}`,
     * because each is computed from state the config DSL cannot reach, is a
     * shape it cannot build, or comes from a second network call. They are
     * merged in ahead of resolution, except the server-authored text in
     * `VERBATIM_PANEL_PROPS`, which is assigned after it.
     *
     * Ahead of it, not after, so the injected order goes through
     * `maskPricesForDisplay` with everything else. Injecting afterwards, the
     * way the MinAmount message below is injected, would hand a hide-prices
     * shopper the real totals; that one is safe there only because it is a
     * string with no money in it.
     *
     * Config still owns every label, the state copy, the currency, the status
     * map, `fieldsToDisplay`, the slots and the actions.
     */
    // No `importRoute &&` guard: the field is declared non-optional `string`,
    // so the check was one the type forbids reaching, and `Map.get(undefined)`
    // returns `undefined` anyway.
    const injectPanelProps = PANEL_PROP_INJECTORS.get(rest.importRoute)
    const { toResolve, verbatim } = splitVerbatim(
      injectPanelProps
        ? { ...props, ...injectPanelProps(state, props) }
        : props,
      VERBATIM_PANEL_PROPS.get(rest.importRoute) ?? [],
    )
    const resolvedProps = getCinnamonProps(
      toResolve,
      state,
      eventEmitter,
      isMinAmount || isCartConditionBlocked,
    )
    // Server-authored text, assigned after resolution so a `{{…}}` in it is
    // shown as written rather than resolved (`VERBATIM_PANEL_PROPS`).
    Object.assign(resolvedProps, verbatim)

    if (isMinAmount) {
      const message = getMinAmountProgressMessage(state, resolvedProps)
      if (message !== undefined) {
        resolvedProps.message = message
      }
    }

    /*
     * The server-side cart-condition rule verdict (COM-1213).
     *
     * Reads `checkoutSlice.lastAppliedRules` through
     * `getCartConditionBlockedAlert` and overwrites `show`/`message` on the
     * Alert's resolved props. The authored `AlertCartConditionBlocked` default
     * seeds both at `false`/`''`, so an unauthored copy key (which the
     * selector reads to opt-out) leaves the Alert hidden.
     *
     * `preserveTotal: true` above is deliberate — the message carries a
     * currency literal substituted from the server rule's `data.quantity`, and
     * `maskPricesForDisplay` would otherwise redact it on a `hidePrices`
     * session. `getMinAmountProgressMessage` relies on the same escape, with
     * the same reason spelled out above the branch.
     */
    if (isCartConditionBlocked) {
      const alert = getCartConditionBlockedAlert(state)
      resolvedProps.show = alert.show
      resolvedProps.message = alert.message
    }

    /*
     * Render-time suppression for the three legacy body Alerts (MinQty,
     * MaxQty, MaxAmount) and the footer MinAmount — review of #826.
     *
     * Gated on **container presence** of the new `comp-cart-condition-blocked`
     * component. A storefront whose authored body-components array omits it is
     * running the legacy UI, with no replacement surface, and must keep its
     * local warning visible. The round-2 review of #826 caught this: a custom
     * layout without the new component was losing its MinAmount/qty/max
     * warning with nothing in its place.
     *
     * Suppresses the local Alert's `show` the moment the new surface is about
     * to render for the same rule type, regardless of the `cartAlerts.isActive`
     * placeholder's current value — closing the window between a pre-flight
     * answer arriving and the next `onCartResponse` where both alerts would
     * otherwise render side by side. The gate is per rule type, so other
     * still-blocking rules continue to show their local warnings.
     */
    if (hasCartConditionBlockedAlertComponent) {
      const rendering = getCartConditionBlockedAlert(state)
      if (rendering.show && rendering.ruleType) {
        const localRule = LOCAL_CART_CONDITION_ALERT_UID_TO_RULE.get(rest.uid)
        if (localRule === rendering.ruleType) {
          resolvedProps.show = false
        }
        if (
          isMinAmount &&
          rendering.ruleType === CheckoutRules.CART_CONDITION_PENDING_MIN_AMOUNT
        ) {
          resolvedProps.show = false
        }
      }
    }

    return { ...rest, props: resolvedProps }
  }

const processDataValidated = (
  data: UnresolvedRecord | undefined = {},
  state: RootState,
) => resolveProps(data, state)

const hasCartConditionBlockedAlertIn = (
  components: readonly Icomponent[] | undefined,
): boolean =>
  components?.some(
    (c) => c.uid === CART_CONDITION_BLOCKED_ALERT_UID,
  ) ?? false

export const getCinnamonPropsContainer = (
  { header, body, footer, modals, uid }: IContainers,
  state: RootState,
  eventEmitter: EventEmitter,
): IProcessedContainer => {
  /*
   * Whether this container holds the new `comp-cart-condition-blocked` alert
   * (COM-1213). Only in that case may `mapComponent` suppress a local
   * MinQty/MaxQty/MaxAmount/MinAmount Alert — a container without the new
   * component has no replacement surface, and must keep its local warnings.
   */
  const hasCartConditionBlockedAlertComponent =
    hasCartConditionBlockedAlertIn(header?.components) ||
    hasCartConditionBlockedAlertIn(body?.components) ||
    hasCartConditionBlockedAlertIn(footer?.components)
  const toProcessedComponent = mapComponent(
    state,
    eventEmitter,
    hasCartConditionBlockedAlertComponent,
  )
  return {
    uid,
    modals,
    header: {
      props: processDataValidated(header?.props, state),
      components:
        header?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
    body: {
      props: processDataValidated(body?.props, state),
      components:
        body?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
    footer: {
      props: processDataValidated(footer?.props, state),
      components:
        footer?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
  }
}

export default getCinnamonProps
